const createPortalInfoController = function ({
  $ = window.jQuery,
  $body = $('body'),
  baseSwal = window.Swal,
  fnT = function (key) { return String(key || '') },
  fetchImpl = fetch,
  windowRef = window,
  appVersionCheckIntervalMs: APP_VERSION_CHECK_INTERVAL_MS = (5 * 60 * 1000),
  readmeCacheTtlMs: PORTAL_README_CACHE_TTL_MS = (60 * 1000),
} = {}) {

  const portalInfoState = {
    baselineVersion: '',
    latestVersion: '',
    lastNotifiedVersion: '',
    electronAppVersion: '',
    electronAppDisplayName: '',
    electronVersionMismatchNotified: false,
    readmeCache: null,
    readmeCacheAt: 0
  }

  const readElectronAppInfo = async function () {
    if (!(windowRef && windowRef.electronAPI && typeof windowRef.electronAPI.getAppInfo === 'function')) return null
    try {
      const appInfo = await windowRef.electronAPI.getAppInfo()
      const appVersion = String(appInfo && appInfo.appVersion ? appInfo.appVersion : '').trim()
      const appDisplayName = String(appInfo && appInfo.appDisplayName ? appInfo.appDisplayName : '').trim()
      portalInfoState.electronAppVersion = appVersion
      portalInfoState.electronAppDisplayName = appDisplayName
      return {
        appVersion: appVersion,
        appDisplayName: appDisplayName
      }
    } catch (error) {
      console.warn(fnT('log.version_check_error'), error)
      return null
    }
  }

  const triggerElectronUpdateCheck = async function () {
    if (!(windowRef && windowRef.electronAPI && typeof windowRef.electronAPI.checkForUpdates === 'function')) return
    try {
      await windowRef.electronAPI.checkForUpdates()
    } catch (error) {
      console.warn(fnT('log.version_check_error'), error)
    }
  }

  const fetchReadmeDocs = async function (forceRefresh) {
    const now = Date.now()
    if (!forceRefresh && portalInfoState.readmeCache && (now - portalInfoState.readmeCacheAt) < PORTAL_README_CACHE_TTL_MS) {
      return portalInfoState.readmeCache
    }
    const response = await fetchImpl('/api/docs/readme', { cache: 'no-store' })
    if (!response.ok) throw new Error(fnT('readme.endpoint_error') + ': ' + response.status)
    const payload = await response.json()
    portalInfoState.readmeCache = payload
    portalInfoState.readmeCacheAt = now
    return payload
  }

  const setInfoButtonVersionAlert = function (isActive) {
    const active = !!isActive
    $body.find('[data-leaf-info-menu-toggle]').each(function () {
      const $btn = $(this)
      const $icon = $btn.find('i, svg').first()
      if (active) {
        $btn.removeClass('btn-outline-dark')
        $btn.addClass('btn-success text-white border-success')
        $icon.addClass('fa-beat')
        return
      }
      $btn.removeClass('btn-success text-white border-success')
      $btn.addClass('btn-outline-dark')
      $icon.removeClass('fa-beat')
    })
    $body.find('[data-leaf-info]').each(function () {
      const $item = $(this)
      const $icon = $item.find('i, svg').first()
      $item.removeClass('text-success fw-semibold bg-success-subtle')
      $icon.removeClass('fa-beat')
    })
    const $readmeItem = $body.find('[data-leaf-info][data-info-tab="readme"]')
    if (active && $readmeItem.length) {
      $readmeItem.addClass('text-success fw-semibold bg-success-subtle')
      $readmeItem.find('i, svg').first().addClass('fa-beat')
    }
  }

  const showVersionUpdateToast = function (nextVersion) {
    const versionText = String(nextVersion || '').trim()
    if (!versionText || portalInfoState.lastNotifiedVersion === versionText) return
    portalInfoState.lastNotifiedVersion = versionText
    if (!baseSwal || typeof baseSwal.fire !== 'function') return
    baseSwal.fire({
      toast: true,
      position: 'bottom-end',
      icon: 'info',
      title: fnT('info.update_available'),
      text: fnT('info.refresh_hint'),
      timer: 0,
      timerProgressBar: false,
      showConfirmButton: false,
      showCloseButton: true
    })
  }

  const fetchAppVersion = async function () {
    const response = await fetchImpl('/api/app-version', { cache: 'no-store' })
    if (!response.ok) throw new Error(fnT('info.version_endpoint_error') + ': ' + response.status)
    const payload = await response.json()
    return String(payload && payload.version ? payload.version : '').trim()
  }

  const checkForAppVersionUpdate = async function () {
    try {
      await readElectronAppInfo()
      await triggerElectronUpdateCheck()
      const version = await fetchAppVersion()
      if (!version) return
      if (!portalInfoState.baselineVersion) {
        portalInfoState.baselineVersion = version
        portalInfoState.latestVersion = version
        setInfoButtonVersionAlert(false)
        return
      }
      portalInfoState.latestVersion = version
      const hasUpdate = version !== portalInfoState.baselineVersion
      setInfoButtonVersionAlert(hasUpdate)
      if (hasUpdate) {
        showVersionUpdateToast(version)
      }
    } catch (error) {
      console.warn(fnT('log.version_check_error'), error)
    }
  }

  const initAppVersionWatcher = function () {
    checkForAppVersionUpdate()
    if (windowRef && typeof windowRef.setInterval === 'function') {
      windowRef.setInterval(checkForAppVersionUpdate, APP_VERSION_CHECK_INTERVAL_MS)
    }
  }

  const hasAppVersionUpdate = function () {
    const baseline = String(portalInfoState.baselineVersion || '').trim()
    const latest = String(portalInfoState.latestVersion || '').trim()
    if (!baseline || !latest) return false
    return baseline !== latest
  }

  const initVersionDebugApi = function () {
    if (!windowRef) return
    windowRef.PORTAL_CHECK_VERSION_NOW = async function () {
      await checkForAppVersionUpdate()
      return {
        baselineVersion: portalInfoState.baselineVersion,
        latestVersion: portalInfoState.latestVersion,
        hasUpdate: hasAppVersionUpdate()
      }
    }
    windowRef.PORTAL_TEST_VERSION_UPDATE = function (nextVersion) {
      const baseline = String(portalInfoState.baselineVersion || '').trim()
      if (!baseline) {
        console.warn(fnT('log.portal_test_baseline_missing'))
        return null
      }
      const simulated = String(nextVersion || '').trim() || (baseline + String(fnT('version.test_suffix') || '-teszt'))
      portalInfoState.latestVersion = simulated
      setInfoButtonVersionAlert(true)
      showVersionUpdateToast(simulated)
      return {
        baselineVersion: baseline,
        latestVersion: simulated,
        hasUpdate: true
      }
    }
  }

  const initElectronAutoUpdate = function () {
    if (!(windowRef && windowRef.electronAPI && typeof windowRef.electronAPI.onUpdateAvailable === 'function')) return

    if (typeof windowRef.electronAPI.onCheckForUpdatesMenu === 'function') {
      windowRef.electronAPI.onCheckForUpdatesMenu(function () {
        checkForAppVersionUpdate()
      })
    }

    windowRef.electronAPI.onUpdateAvailable(function (info) {
      const version = String(info && info.version ? info.version : '').trim()
      if (!baseSwal || typeof baseSwal.fire !== 'function') return
      baseSwal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'info',
        title: fnT('info.electron_update_available_title'),
        text: fnT('info.electron_update_downloading').replace('{version}', version || '...'),
        timer: 0,
        timerProgressBar: false,
        showConfirmButton: false,
        showCloseButton: true
      })
    })

    windowRef.electronAPI.onUpdateDownloaded(function (info) {
      const version = String(info && info.version ? info.version : '').trim()
      if (!baseSwal || typeof baseSwal.fire !== 'function') return
      baseSwal.fire({
        icon: 'success',
        title: fnT('info.electron_update_ready_title'),
        text: fnT('info.electron_update_ready_text').replace('{version}', version || ''),
        confirmButtonText: fnT('info.electron_update_install'),
        showCancelButton: true,
        cancelButtonText: fnT('info.electron_update_later')
      }).then(function (result) {
        if (result.isConfirmed && windowRef.electronAPI && typeof windowRef.electronAPI.installUpdate === 'function') {
          windowRef.electronAPI.installUpdate()
        }
      })
    })

    if (typeof windowRef.electronAPI.onUpdateNotAvailable === 'function') {
      windowRef.electronAPI.onUpdateNotAvailable(function () {
        if (!baseSwal || typeof baseSwal.fire !== 'function') return
        baseSwal.fire({
          toast: true,
          position: 'bottom-end',
          icon: 'success',
          title: fnT('info.electron_update_not_available_title'),
          text: fnT('info.electron_update_not_available_text')
        })
      })
    }
  }

  const parseLatestInstallerFileName = function (manifestText) {
    const raw = String(manifestText || '')
    const lines = raw.split(/\r?\n/)
    let fallback = ''

    for (const line of lines) {
      const fileMatch = line.match(/^\s*-\s*url:\s*(.+)\s*$/)
      if (fileMatch && fileMatch[1]) {
        const candidate = String(fileMatch[1]).trim().replace(/^['\"]|['\"]$/g, '')
        if (candidate) return candidate
      }
      const pathMatch = line.match(/^\s*path:\s*(.+)\s*$/)
      if (pathMatch && pathMatch[1]) {
        const candidate = String(pathMatch[1]).trim().replace(/^['\"]|['\"]$/g, '')
        if (candidate) fallback = candidate
      }
    }

    return fallback
  }

  const toDownloadPath = function (relativePath) {
    const normalized = String(relativePath || '').replace(/\\/g, '/').replace(/^\/+/, '')
    if (!normalized) return ''
    const encoded = normalized.split('/').map(function (segment) {
      return encodeURIComponent(segment)
    }).join('/')
    return '/downloads/electron/' + encoded
  }

  const resolveLatestElectronInstallerPath = async function () {
    const response = await fetchImpl('/downloads/electron/latest.yml', { cache: 'no-store' })
    if (!response.ok) throw new Error(fnT('info.electron_download_manifest_error') + ': ' + response.status)
    const manifestText = await response.text()
    const installerRelativePath = parseLatestInstallerFileName(manifestText)
    const installerDownloadPath = toDownloadPath(installerRelativePath)
    if (!installerDownloadPath) throw new Error(fnT('info.electron_download_missing_file'))
    return installerDownloadPath
  }

  const initElectronDownloadAction = function () {
    if (!$body || typeof $body.off !== 'function') return
    $body
      .off('click.portalElectronDownload', '[data-leaf-electron-download]')
      .on('click.portalElectronDownload', '[data-leaf-electron-download]', async function () {
        try {
          const downloadPath = await resolveLatestElectronInstallerPath()
          if (windowRef && typeof windowRef.open === 'function') {
            windowRef.open(downloadPath, '_blank', 'noopener')
          }
        } catch (error) {
          console.warn(fnT('log.electron_download_error'), error)
          if (!baseSwal || typeof baseSwal.fire !== 'function') return
          baseSwal.fire({
            icon: 'error',
            title: fnT('layout.main.info.electron_download'),
            text: fnT('info.electron_download_failed')
          })
        }
      })
  }

  return {
    fetchReadmeDocs: fetchReadmeDocs,
    setInfoButtonVersionAlert: setInfoButtonVersionAlert,
    showVersionUpdateToast: showVersionUpdateToast,
    readElectronAppInfo: readElectronAppInfo,
    fetchAppVersion: fetchAppVersion,
    checkForAppVersionUpdate: checkForAppVersionUpdate,
    initAppVersionWatcher: initAppVersionWatcher,
    hasAppVersionUpdate: hasAppVersionUpdate,
    initVersionDebugApi: initVersionDebugApi,
    initElectronAutoUpdate: initElectronAutoUpdate,
    initElectronDownloadAction: initElectronDownloadAction
  }
}

const bindInfoModalHandler = function ({
  $ = window.jQuery,
  $body = $('body'),
  areaSwal = window.Swal,
  fnT = function (key) { return String(key || '') },
  fnCheckForAppVersionUpdate = async function () {},
  fnFetchReadmeDocs = async function () { return null },
  fnHasAppVersionUpdate = function () { return false },
  fnRenderReadmePanel = function () {},
  fnGetLayerVersions = function () { return {} },
} = {}) {

  if (!$body || typeof $body.data !== 'function') return
  if ($body.data('portalInfoModalBound')) return
  $body.data('portalInfoModalBound', true)

  $body.find('[data-leaf-info]').on('click', async function () {
    const requestedTab = String($(this).data('infoTab') || '').trim().toLowerCase()
    let readmePayload = null
    try {
      await fnCheckForAppVersionUpdate()
      readmePayload = await fnFetchReadmeDocs(false)
    } catch (error) {
      console.warn(fnT('log.info_payload_load_error'), error)
    }
    let readmeActive = fnHasAppVersionUpdate()
    if (requestedTab === 'readme') readmeActive = true
    if (requestedTab === 'json') readmeActive = false
    const jsonActive = !readmeActive
    areaSwal.fire({
      width: 1000,
      title: fnT('info.title'),
      html: '' +
        '<div class="portal-info-modal">' +
          '<ul class="nav nav-tabs mb-3" role="tablist">' +
            '<li class="nav-item" role="presentation">' +
              '<button class="nav-link' + (readmeActive ? ' active' : '') + '" id="portal-info-tab-readme" data-bs-toggle="tab" data-bs-target="[data-portal-info-pane-readme]" type="button" role="tab" aria-controls="portal-info-pane-readme" aria-selected="' + (readmeActive ? 'true' : 'false') + '">' + fnT('info.tab_about') + '</button>' +
            '</li>' +
            '<li class="nav-item" role="presentation">' +
              '<button class="nav-link' + (jsonActive ? ' active' : '') + '" id="portal-info-tab-json" data-bs-toggle="tab" data-bs-target="[data-portal-info-pane-json]" type="button" role="tab" aria-controls="portal-info-pane-json" aria-selected="' + (jsonActive ? 'true' : 'false') + '">' + fnT('info.tab_versions') + '</button>' +
            '</li>' +
          '</ul>' +
          '<div class="tab-content">' +
            '<div class="tab-pane fade' + (readmeActive ? ' show active' : '') + '" id="portal-info-pane-readme" data-portal-info-pane-readme role="tabpanel" aria-labelledby="portal-info-tab-readme"></div>' +
            '<div class="tab-pane fade' + (jsonActive ? ' show active' : '') + '" id="portal-info-pane-json" data-portal-info-pane-json role="tabpanel" aria-labelledby="portal-info-tab-json"></div>' +
          '</div>' +
        '</div>',
      didOpen: function (modal) {
        const $cont = $(modal).find('.swal2-html-container').addClass('text-start')
        const $readmePane = $cont.find('[data-portal-info-pane-readme]')
        const $jsonPane = $cont.find('[data-portal-info-pane-json]')

        const $jsonWrap = $('<div class="mb-1"></div>')
        const $jsonTitle = $('<h6 class="mb-2"></h6>').text(fnT('info.tab_versions'))
        const $json = $('<div class="portal-info-json"></div>')
        $json.JSONView(fnGetLayerVersions(), { collapsed: false })
        $jsonWrap.append($jsonTitle).append($json)

        const $readmeWrap = $('<div class="mb-1"></div>')
        const $readmeTitle = $('<h6 class="mb-2"></h6>').text(fnT('info.tab_about'))
        const $readmeHost = $('<div class="portal-info-readme"></div>')
        $readmeWrap.append($readmeTitle).append($readmeHost)

        $readmePane.append($readmeWrap)
        $jsonPane.append($jsonWrap)
        fnRenderReadmePanel($readmeHost, readmePayload)
      }
    })
  })
}

export {
  createPortalInfoController,
  bindInfoModalHandler
}
